import { useEffect, useState } from 'react';
import './EditReportPage.css';

// Printable summary of one network-edit simulation. Payload arrives via localStorage;
// "Save as PDF" is the browser's print dialog, so there is no PDF dependency.

const fmt = (n, d = 0) =>
  n == null ? '—' : Number(n).toLocaleString(undefined, {
    minimumFractionDigits: d, maximumFractionDigits: d,
  });

function Delta({ before, after, unit = '', decimals = 0, lowerIsBetter = true }) {
  if (before == null || after == null) return <span>—</span>;
  const d = after - before;
  const pct = before ? (d / before) * 100 : 0;
  const good = lowerIsBetter ? d < 0 : d > 0;
  const cls = Math.abs(pct) < 0.05 ? 'flat' : good ? 'better' : 'worse';
  return (
    <span className={`rp-delta ${cls}`}>
      {d > 0 ? '+' : ''}{fmt(d, decimals)}{unit}
      {before ? ` (${d > 0 ? '+' : ''}${pct.toFixed(1)}%)` : ''}
    </span>
  );
}

/** A conflict-count delta. No percentage when the baseline is below 10: conflict counts
 *  are small integers (PET is single-digit per road) and "+50%" off a baseline of 2 is
 *  noise. Below the floor the absolute change is shown alone. */
const PCT_FLOOR = 10;

/** Save the report payload as a .json file. */
function downloadJson(payload, region) {
  const stamp = new Date(payload?.generatedAt || Date.now())
    .toISOString().replace(/[:.]/g, '-').slice(0, 19);
  const blob = new Blob([JSON.stringify(payload, null, 2)],
    { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${(region || 'network-edit').toLowerCase().replace(/\W+/g, '-')}-report-${stamp}.json`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoking immediately can race the download in some browsers; a tick is
  // enough and the object is small.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function SafetyDelta({ before, after, decimals = 0 }) {
  if (before == null || after == null) return <span className="rp-muted">—</span>;
  const d = after - before;
  if (d === 0) return <span className="rp-delta flat">no change</span>;
  const cls = d < 0 ? 'better' : 'worse';
  const showPct = before >= PCT_FLOOR;
  const pct = before ? (d / before) * 100 : 0;
  return (
    <span className={`rp-delta ${cls}`}>
      {d > 0 ? '+' : ''}{fmt(d, decimals)}
      {showPct ? ` (${d > 0 ? '+' : ''}${pct.toFixed(1)}%)` : ''}
    </span>
  );
}

export default function EditReportPage() {
  const [payload, setPayload] = useState(null);

  useEffect(() => {
    const raw = localStorage.getItem('networkEditReport');
    if (raw) {
      try { setPayload(JSON.parse(raw)); } catch { /* malformed, show empty state */ }
    }
  }, []);

  if (!payload) {
    return <div className="rp-empty">No report data. Run a simulation, then choose “Report”.</div>;
  }

  const { region, hour, weekday, demandScale, generatedAt, edits, before, after,
          changed, signal, runSeconds, vehicles, safety, safetyBefore,
          safetyError, signalError, safetyBlocked, safetyPending } = payload;

  // The two columns differ in exactly one case — a demand run, whose only "before" is
  // the bake at the bake's own multiplier. Older payloads have no `beforeScale`.
  const beforeScale = payload.beforeScale ?? demandScale;
  const mixedScale = beforeScale !== demandScale;
  // A change column across two multipliers is mostly the multiplier: at x1 the baked
  // baseline has 0 congested roads and at x5 it has 98.
  const MixedScaleNote = () => (mixedScale ? (
    <p className="rp-note">
      <b>The two columns ran at different demand multipliers</b> (×{beforeScale}
      {' '}against ×{demandScale}). The “before” side is the baked baseline,
      which is the only one that exists until a run at this multiplier has been
      made. <b>Read the Change column as the multiplier plus the edit, not as
      the edit.</b> To compare like with like, apply this demand once, then make
      the edit — the second run is measured against the first.
    </p>
  ) : null);

  return (
    <div className="rp-page">
      <div className="rp-actions">
        <button onClick={() => window.print()}>Save as PDF / Print</button>
        {/* Blob, not a data: URI — the payload carries every changed edge. */}
        <button onClick={() => downloadJson(payload, region)}>Download JSON</button>
      </div>

      <header className="rp-header">
        <h1>Network Edit — Simulation Report</h1>
        <div className="rp-sub">
          {region} · weekday {weekday}, {hour}:00–{hour + 1}:00 · demand ×{demandScale}
        </div>
        <div className="rp-meta">
          Generated {new Date(generatedAt).toLocaleString()} ·
          {' '}{fmt(vehicles)} trips assigned{runSeconds ? ` · ${runSeconds}s run` : ''}
        </div>
      </header>

      <section>
        <h2>Edits applied</h2>
        {edits?.length ? (
          <ul className="rp-edits">
            {edits.map((e, i) => (
              <li key={i}><b>{e.label}</b> — {e.detail}</li>
            ))}
          </ul>
        ) : <p className="rp-muted">No edits — this is the unmodified network.</p>}
      </section>

      <section>
        <h2>Network totals</h2>
        <table className="rp-table">
          <thead>
            {/* The label belongs where the numbers are: a printed page is read one section at a
                time. See `beforeScale` in MapContainer. */}
            <tr><th>Measure</th><th>Before (×{beforeScale})</th>
              <th>After (×{demandScale})</th><th>Change</th></tr>
          </thead>
          <tbody>
            <tr>
              <td>Congested roads (V/C ≥ 0.9)</td>
              <td>{fmt(before?.congested_edges)}</td>
              <td>{fmt(after?.congested_edges)}</td>
              <td><Delta before={before?.congested_edges} after={after?.congested_edges} /></td>
            </tr>
            <tr>
              <td>Vehicle miles travelled</td>
              <td>{fmt(before?.total_vmt_mi)} mi</td>
              <td>{fmt(after?.total_vmt_mi)} mi</td>
              <td><Delta before={before?.total_vmt_mi} after={after?.total_vmt_mi} unit=" mi" /></td>
            </tr>
            <tr>
              <td>Mean V/C</td>
              <td>{fmt(before?.mean_vc, 3)}</td>
              <td>{fmt(after?.mean_vc, 3)}</td>
              <td><Delta before={before?.mean_vc} after={after?.mean_vc} decimals={3} /></td>
            </tr>
          </tbody>
        </table>
        <MixedScaleNote />
      </section>

      {changed?.worse?.length > 0 && (
        <section>
          <h2>Roads that absorbed traffic</h2>
          <table className="rp-table">
            <thead><tr><th>Road</th><th>V/C before</th><th>V/C after</th><th>LOS</th></tr></thead>
            <tbody>
              {changed.worse.map((r) => (
                <tr key={r.id}>
                  <td>{r.name || r.id}</td>
                  <td>{r.vcBefore.toFixed(2)}</td>
                  <td className="worse">{r.vcAfter.toFixed(2)}</td>
                  <td>{r.losAfter}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {changed?.better?.length > 0 && (
        <section>
          <h2>Roads that were relieved</h2>
          <table className="rp-table">
            <thead><tr><th>Road</th><th>V/C before</th><th>V/C after</th><th>LOS</th></tr></thead>
            <tbody>
              {changed.better.map((r) => (
                <tr key={r.id}>
                  <td>{r.name || r.id}</td>
                  <td>{r.vcBefore.toFixed(2)}</td>
                  <td className="better">{r.vcAfter.toFixed(2)}</td>
                  <td>{r.losAfter}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {signal && (
        <section>
          <h2>Signal retiming — {signal.junction_label || signal.junction_id}</h2>
          <table className="rp-table">
            <thead><tr><th>Approach</th><th>Wait before</th><th>Wait after</th><th>Change</th></tr></thead>
            <tbody>
              {signal.approaches.map((a) => (
                <tr key={a.edge_id}>
                  <td>{a.name || a.edge_id}</td>
                  <td>{fmt(a.before.waiting_s)} s</td>
                  <td>{fmt(a.after.waiting_s)} s</td>
                  <td className={a.waiting_delta_pct > 0 ? 'worse' : 'better'}>
                    {a.waiting_delta_pct > 0 ? '+' : ''}{a.waiting_delta_pct}%
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="rp-note">
            Measured over a {signal.window_min}-minute microsimulation. Total approach
            delay {signal.totals.delta_pct > 0 ? '+' : ''}{signal.totals.delta_pct}% —
            green time is zero-sum, so retiming moves delay between approaches rather
            than removing it.
          </p>
        </section>
      )}

      {!safety && safetyBlocked && (
        <section>
          <h2>Safety — not analysed for this demand</h2>
          <p className="rp-note">{safetyBlocked}</p>
        </section>
      )}

      {!safety && !safetyBlocked && safetyPending && (
        <section>
          <h2>Safety — being calculated</h2>
          <p className="rp-note">
            The conflict analysis runs a 15-minute microsimulation and finishes
            after the traffic results. It is still running.{' '}
            <b>Reopen this report from the editor when it is done</b> and this
            section will carry the numbers.
          </p>
        </section>
      )}

      {!safety && !safetyBlocked && !safetyPending && safetyError && (
        <section>
          <h2>Safety — not available for this run</h2>
          <p className="rp-note">{safetyError}</p>
          {signalError && <p className="rp-note">{signalError}</p>}
        </section>
      )}

      {safety && (
        <section>
          {/* Each measure is named in full with its abbreviation on first appearance; every
              later mention is the abbreviation alone. */}
          <h2>Safety — Surrogate Safety Measures (SSM)</h2>
          <table className="rp-table">
            <thead>
              <tr><th>Measure</th><th>Before (×{beforeScale})</th>
                <th>After (×{demandScale})</th><th>Change</th></tr>
            </thead>
            <tbody>
              {/* Each measure counts encounters crossing ITS OWN threshold; never add them —
                  one near miss can cross two. */}
              <tr>
                <td>Conflicts — Time to Collision (TTC) &lt; 3.0 s</td>
                <td>{fmt(safetyBefore?.conflicts_ttc)}</td>
                <td>{fmt(safety.conflicts_ttc)}</td>
                <td><SafetyDelta before={safetyBefore?.conflicts_ttc} after={safety.conflicts_ttc} /></td>
              </tr>
              <tr>
                <td>— of those, serious (TTC &lt; 1.5 s)</td>
                <td>{fmt(safetyBefore?.conflicts_severe)}</td>
                <td>{fmt(safety.conflicts_severe)}</td>
                <td><SafetyDelta before={safetyBefore?.conflicts_severe} after={safety.conflicts_severe} /></td>
              </tr>
              <tr>
                <td>Conflicts — Post-Encroachment Time (PET) &lt; 2.0 s</td>
                <td>{fmt(safetyBefore?.conflicts_pet)}</td>
                <td>{fmt(safety.conflicts_pet)}</td>
                <td><SafetyDelta before={safetyBefore?.conflicts_pet} after={safety.conflicts_pet} /></td>
              </tr>
              <tr>
                <td>Conflicts — Deceleration Rate to Avoid a Crash (DRAC) &gt; 3.4 m/s²</td>
                <td>{fmt(safetyBefore?.conflicts_drac)}</td>
                <td>{fmt(safety.conflicts_drac)}</td>
                <td><SafetyDelta before={safetyBefore?.conflicts_drac} after={safety.conflicts_drac} /></td>
              </tr>
              <tr>
                <td>Conflict rate (TTC per 1,000 vehicle-km)</td>
                <td>{fmt(safetyBefore?.conflict_rate, 1)}</td>
                <td>{fmt(safety.conflict_rate, 1)}</td>
                <td><SafetyDelta before={safetyBefore?.conflict_rate} after={safety.conflict_rate} decimals={1} /></td>
              </tr>
              <tr>
                <td>Median / worst minimum TTC</td>
                <td>{fmt(safetyBefore?.min_ttc_p50, 2)} s / {fmt(safetyBefore?.min_ttc_min, 2)} s</td>
                <td>{fmt(safety.min_ttc_p50, 2)} s / {fmt(safety.min_ttc_min, 2)} s</td>
                <td className="rp-muted">—</td>
              </tr>
            </tbody>
          </table>
          <MixedScaleNote />

          {safety.top_edges?.length > 0 && (
            <>
              <h3 className="rp-subhead">Where conflicts concentrate</h3>
              <table className="rp-table">
                <thead>
                  <tr>
                    <th>Road</th>
                    <th title="Time to Collision &lt; 3.0 s">TTC</th>
                    <th title="Time to Collision &lt; 1.5 s">Serious</th>
                    <th title="Post-Encroachment Time &lt; 2.0 s">PET</th>
                    <th title="Deceleration Rate to Avoid a Crash &gt; 3.4 m/s²">DRAC</th>
                  </tr>
                </thead>
                <tbody>
                  {safety.top_edges.map((e) => (
                    <tr key={e.edge}>
                      <td>{e.name || e.edge}</td>
                      <td>{fmt(e.ttc)}</td>
                      <td className={e.severe > 0 ? 'worse' : ''}>{fmt(e.severe)}</td>
                      <td>{fmt(e.pet)}</td>
                      <td>{fmt(e.drac)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}

          {/* A conflict count is not a crash count, and 15 minutes on one seed is not a safety
              study. */}
          <p className="rp-note">
            These are <b>surrogate</b> measures: near misses SUMO counted while
            simulating {safety.params?.window_s ? safety.params.window_s / 60 : 15} minutes
            of traffic, not observed or predicted crashes. They come from a single
            simulation run on modelled demand that has not been calibrated against
            traffic counts, so read the <i>direction and size</i> of a change, not
            the absolute number. One encounter can cross more than one threshold,
            so the rows above are never added together.
            {safety.collisions > 0 && (
              <> The simulation also recorded {fmt(safety.collisions)} outright
              collisions; in SUMO these are usually a sign of demand that exceeds
              what the network can physically take, not a prediction.</>
            )}
          </p>
        </section>
      )}

      <section className="rp-method">
        <h2>Method and limitations</h2>
        <p>
          Edits are compiled into the road network with <code>netconvert</code>, traffic is
          re-assigned with <code>duarouter</code>, and volume-to-capacity ratios are computed
          per road (capacity = lanes × 1800 veh/h, discounted by how much road each
          vehicle takes — the <code>pce</code> value carried by every vehicle type,
          two for a bus or a heavy truck and one for anything on four tyres — and
          travel time from the standard BPR curve). Signal effects come from a SUMO
          microsimulation, because static assignment does not model signal timing.
        </p>
        <ul>
          <li>Demand is multiplied ×{demandScale} — one multiplier for the traffic
              assignment and the conflict microsimulation alike — and has <b>not
              been calibrated against traffic counts</b>; treat absolute volumes as
              indicative and comparisons as the meaningful output.</li>
          <li>Signal timings in the base network are synthesised defaults, not the
              agency's published plans.</li>
          <li>A single hour is modelled; effects outside {hour}:00–{hour + 1}:00 are not represented.</li>
          {safety && (
            <li>Safety uses SUMO's Surrogate Safety Measures (SSM) device over a{' '}
              {safety.params?.window_s ? safety.params.window_s / 60 : 15}-minute
              microsimulation window at thresholds TTC{' '}
              {safety.params?.thresholds?.split(' ')[0] || '3.0'} s, PET{' '}
              {safety.params?.thresholds?.split(' ')[1] || '2.0'} s, DRAC{' '}
              {safety.params?.thresholds?.split(' ')[2] || '3.4'} m/s².
              <b> Surrogate conflicts are not crashes</b> and this is not a safety study.</li>
          )}
        </ul>
      </section>
    </div>
  );
}
